import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Users, Beer, ShoppingBag, CalendarDays, Shield, Target, Tv } from 'lucide-react';
import homeTickerService, { HomeTickerItem } from '../../services/homeTickerService';
import { t } from '../../i18n';

const ACCENT = '#3fced1';

/** Laufgeschwindigkeit: Pixel je Sekunde (langsam, lesbar) */
export const TICKER_PX_PER_SECOND = 28;
/** Unter dieser Breite (px) wird nicht animiert – zu wenig Inhalt zum Laufen */
const MIN_LOOP_WIDTH = 320;

const ICONS: Record<string, React.ComponentType<{ size?: number; style?: React.CSSProperties; className?: string }>> = {
  'tournament-today': Trophy,
  'open-training': Users,
  'pub-open': Beer,
  'shop-open': ShoppingBag,
  'series-event': CalendarDays,
  'league-matchday': Shield,
  'prediction-open': Target,
  'pro-matches': Tv,
};

/** Typ-Beschriftung (i18n) – unbekannte Typen aus neueren Backends fallen auf den Rohtyp zurück */
export function tickerTypeLabel(type: string): string {
  switch (type) {
    case 'tournament-today': return t('web.component.home-today-ticker.turnier-heute');
    case 'open-training': return t('web.component.home-today-ticker.offenes-training');
    case 'pub-open': return t('web.component.home-today-ticker.spielstaette-geoeffnet');
    case 'shop-open': return t('web.component.home-today-ticker.shop-geoeffnet');
    case 'series-event': return t('web.component.home-today-ticker.serien-termin');
    case 'league-matchday': return t('web.component.home-today-ticker.spieltag');
    case 'prediction-open': return t('web.component.home-today-ticker.tippspiel-offen');
    case 'pro-matches': return t('web.component.home-today-ticker.pdc-heute');
    default: return type;
  }
}

/** Haupttext je Eintrag (PDC: Anzahl Spiele statt roher Zahl) */
export function tickerItemText(item: HomeTickerItem): string {
  if (item.type === 'pro-matches') return t('web.component.home-today-ticker.spiele-heute', { count: item.title });
  return item.title;
}

/** Dauer eines Durchlaufs aus der Inhaltsbreite (Sekunden), damit das Tempo unabhängig von der Anzahl gleich bleibt */
export function loopDurationSeconds(contentWidth: number, pxPerSecond = TICKER_PX_PER_SECOND): number {
  if (!Number.isFinite(contentWidth) || contentWidth <= 0) return 0;
  return Math.max(20, Math.round(contentWidth / pxPerSecond));
}

const Pill: React.FC<{ item: HomeTickerItem; ariaHidden?: boolean }> = ({ item, ariaHidden }) => {
  const Icon = ICONS[item.type] ?? CalendarDays;
  const text = tickerItemText(item);
  const meta = [item.subtitle, item.time].filter((s) => typeof s === 'string' && s.trim()).join(' · ');
  return (
    <Link
      to={item.path}
      aria-hidden={ariaHidden || undefined}
      tabIndex={ariaHidden ? -1 : undefined}
      data-testid={ariaHidden ? undefined : 'home-ticker-item'}
      className="group inline-flex h-9 shrink-0 items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900/80 px-3 text-[12px] leading-none text-gray-200 transition-colors hover:border-[#3fced1]/60"
    >
      <Icon size={13} style={{ color: ACCENT }} className="shrink-0" />
      <span className="shrink-0 font-semibold uppercase tracking-wide text-[10px] text-zinc-500">{tickerTypeLabel(item.type)}</span>
      <span className="max-w-[220px] truncate font-semibold text-white">{text}</span>
      {meta && <span className="max-w-[180px] shrink-0 truncate text-zinc-400">{meta}</span>}
    </Link>
  );
};

/**
 * „Heute“-Laufband (Marcel 2026-10-24): flache, niedrige Zeile unter „Neue Spieler“/Slider mit tagesaktuellen
 * Häppchen, die langsam automatisch durchlaufen (CSS-Marquee, Inhalt doppelt für die Endlosschleife).
 * Pausiert bei Hover/Fokus; bei „Bewegung reduzieren“ steht das Band und ist normal scrollbar.
 * Rendert nichts, wenn es heute nichts gibt.
 */
const HomeTodayTicker: React.FC = () => {
  const [items, setItems] = useState<HomeTickerItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [contentWidth, setContentWidth] = useState(0);
  const trackRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await homeTickerService.getHomeTicker();
      if (cancelled) return;
      setItems(res.items);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    setContentWidth(el.scrollWidth);
  }, [items]);

  const reduceMotion = useMemo(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  const animate = !reduceMotion && contentWidth >= MIN_LOOP_WIDTH;
  const duration = loopDurationSeconds(contentWidth);

  if (!loaded || items.length === 0) return null;

  return (
    <section aria-label={t('web.component.home-today-ticker.heute')} className="mt-8 mb-8" data-testid="home-today-ticker">
      <style>{`@keyframes dc-ticker-marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
.dc-ticker:hover .dc-ticker-track,.dc-ticker:focus-within .dc-ticker-track{animation-play-state:paused}`}</style>
      {/* Marcel 24.10.2026: mehr Abstand zum Slider, EINE Überschrift statt „Heute · Was heute los ist“ */}
      <div className="mb-2 flex items-center gap-2">
        <h2 className="section-heading text-base font-bold text-white" data-testid="home-today-ticker-title">{t('web.component.home-today-ticker.was-heute-los-ist')}</h2>
      </div>
      <div className={`dc-ticker relative overflow-hidden ${animate ? '' : 'overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'}`}>
        <div
          className="dc-ticker-track flex w-max gap-2"
          data-testid="home-ticker-track"
          data-animated={animate ? '1' : '0'}
          style={animate ? { animation: `dc-ticker-marquee ${duration}s linear infinite` } : undefined}
        >
          <div ref={trackRef} className="flex gap-2">
            {items.map((item) => <Pill key={item.id} item={item} />)}
          </div>
          {animate && (
            <div className="flex gap-2" aria-hidden="true">
              {items.map((item) => <Pill key={`dup-${item.id}`} item={item} ariaHidden />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HomeTodayTicker;
